// Chemin: C:\gestion-copro\src\components\sentinelle\SentinelleGardienModal.tsx
// Description: Modal affichant le verdict SENTINELLE Gardien avant archivage GED
// Session 65 - Phase 3 GED

import React from 'react';
import {
  CheckCircle,
  XCircle,
  Copy,
  AlertTriangle,
  X,
  FileText,
  ArrowRight,
  ExternalLink,
  Shield
} from 'lucide-react';
import {
  AnalyseGardienResponse,
  DoublonDetecte,
  getVerdictColor,
  getVerdictLabel
} from '../../services/gardien.service';

// ============================================================================
// TYPES
// ============================================================================

interface SentinelleGardienModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  analyse: AnalyseGardienResponse;
  onAccepter: () => void;
  onRejeter: () => void;
  onVoirDoublon?: (documentId: string) => void;
}

// ============================================================================
// COMPOSANTS INTERNES
// ============================================================================

/**
 * Icône du verdict
 */
function VerdictIcon({ verdict }: { verdict: AnalyseGardienResponse['verdict'] }) {
  const baseClasses = 'w-12 h-12';
  
  switch (verdict) {
    case 'accepter':
      return <CheckCircle className={`${baseClasses} text-green-500`} />;
    case 'rejeter':
      return <XCircle className={`${baseClasses} text-red-500`} />;
    case 'doublon':
      return <Copy className={`${baseClasses} text-orange-500`} />;
    case 'verification_requise':
      return <AlertTriangle className={`${baseClasses} text-amber-500`} />;
    default:
      return <FileText className={`${baseClasses} text-gray-500`} />;
  }
}

/**
 * Badge de confiance
 */
function BadgeConfiance({ confiance }: { confiance: number }) {
  let colorClass = 'bg-gray-100 text-gray-700';
  let label = 'Incertain';
  
  if (confiance >= 80) {
    colorClass = 'bg-green-100 text-green-700';
    label = 'Confiance haute';
  } else if (confiance >= 50) {
    colorClass = 'bg-amber-100 text-amber-700';
    label = 'Confiance moyenne';
  } else {
    colorClass = 'bg-red-100 text-red-700';
    label = 'Confiance basse';
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {label} ({confiance}%)
    </span>
  );
}

/**
 * Carte doublon
 */
function DoublonCard({
  doublon,
  onVoir
}: {
  doublon: DoublonDetecte;
  onVoir?: () => void;
}) {
  const similariteColors: Record<string, string> = {
    exact: 'border-red-300 bg-red-50',
    probable: 'border-orange-300 bg-orange-50',
    possible: 'border-amber-300 bg-amber-50'
  };
  
  const similariteLabels: Record<string, string> = {
    exact: 'Doublon exact',
    probable: 'Doublon probable',
    possible: 'Doublon possible'
  };
  
  return (
    <div className={`border rounded-lg p-3 ${similariteColors[doublon.similarite] || 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Copy className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900 truncate">
              {doublon.titre}
            </span>
          </div>
          <p className="text-xs text-gray-600 mb-1">
            {similariteLabels[doublon.similarite] || 'Similarité détectée'}
          </p>
          <p className="text-xs text-gray-500">
            {doublon.raison}
          </p>
        </div>
        {onVoir && (
          <button
            onClick={onVoir}
            className="ml-2 p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
            title="Voir le document existant"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export default function SentinelleGardienModal({
  isOpen,
  onClose,
  fileName,
  analyse,
  onAccepter,
  onRejeter,
  onVoirDoublon
}: SentinelleGardienModalProps) {
  if (!isOpen) return null;

  const verdictColor = getVerdictColor(analyse.verdict);
  const verdictLabel = getVerdictLabel(analyse.verdict);
  
  // Couleurs de fond selon verdict
  const bgColors: Record<string, string> = {
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    orange: 'bg-orange-50 border-orange-200',
    amber: 'bg-amber-50 border-amber-200',
    gray: 'bg-gray-50 border-gray-200'
  };

  // Textes des boutons selon verdict
  const boutonAccepterTexte = analyse.verdict === 'rejeter' 
    ? 'Archiver quand même' 
    : analyse.verdict === 'doublon'
    ? 'Archiver quand même'
    : 'Archiver';
    
  const boutonRejeterTexte = analyse.verdict === 'accepter'
    ? 'Annuler'
    : 'Ne pas archiver';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full transform transition-all">
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                SENTINELLE Gardien
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Corps */}
          <div className="px-6 py-5">
            
            {/* Nom du fichier */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 truncate">
                {fileName}
              </span>
            </div>

            {/* Verdict principal */}
            <div className={`border rounded-xl p-5 mb-4 ${bgColors[verdictColor] || bgColors.gray}`}>
              <div className="flex items-start gap-4">
                <VerdictIcon verdict={analyse.verdict} />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {verdictLabel}
                    </h4>
                    <BadgeConfiance confiance={analyse.confiance} />
                  </div>
                  <p className="text-sm text-gray-700">
                    {analyse.justification}
                  </p>
                </div>
              </div>
            </div>

            {/* Liste des doublons si présents */}
            {analyse.doublons.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-700 mb-2">
                  Documents similaires détectés ({analyse.doublons.length})
                </h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {analyse.doublons.map((doublon) => (
                    <DoublonCard
                      key={doublon.documentId}
                      doublon={doublon}
                      onVoir={onVoirDoublon ? () => onVoirDoublon(doublon.documentId) : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Métadonnées analyse */}
            <div className="text-xs text-gray-400 flex items-center gap-2">
              <span>Analyse : {analyse.metadata.methodeAnalyse}</span>
              <span>•</span>
              <span>{analyse.metadata.tempsAnalyseMs} ms</span>
            </div>

          </div>

          {/* Footer avec boutons */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onRejeter}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {boutonRejeterTexte}
            </button>
            <button
              onClick={onAccepter}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                analyse.verdict === 'rejeter' || analyse.verdict === 'doublon'
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {boutonAccepterTexte}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
